import { supabase } from '@/api/supabaseClient';

// Auth surface shaped like the Base44 SDK's, mapped onto Supabase Auth.
// The 12-method correspondence is documented in
// docs/migration-to-supabase.md §7.1.

const PROFILE_COLUMNS =
  'id, display_name, full_name, bio, avatar_url, role, review_count, badge_level, created_at';

function redirectUrl(path = '/') {
  return new URL(path, window.location.origin).toString();
}

// The app's notion of "the current user" is the auth record and its profile
// row flattened into one object, which is the shape auth.me() used to return.
async function hydrate(authUser) {
  if (!authUser) return null;

  const { data: profile, error } = await supabase
    .from('profiles')
    .select(PROFILE_COLUMNS)
    .eq('id', authUser.id)
    .maybeSingle();

  if (error) throw error;

  return {
    id: authUser.id,
    email: authUser.email,
    // handle_new_user() seeds the profile from the auth metadata, but a row
    // created moments ago may not have landed yet — fall back to the token.
    display_name: profile?.display_name ?? authUser.user_metadata?.display_name ?? '',
    full_name: profile?.full_name ?? authUser.user_metadata?.full_name ?? '',
    bio: profile?.bio ?? '',
    avatar_url: profile?.avatar_url ?? authUser.user_metadata?.avatar_url ?? '',
    role: profile?.role ?? 'user',
    review_count: profile?.review_count ?? 0,
    badge_level: profile?.badge_level ?? 'none',
    created_date: profile?.created_at ?? null,
  };
}

export async function me() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data?.user) throw new Error('Not authenticated');
  return hydrate(data.user);
}

// Same as me(), but a signed-out visitor is a null result rather than a throw.
export async function currentUser() {
  const { data } = await supabase.auth.getSession();
  if (!data?.session?.user) return null;
  try {
    return await hydrate(data.session.user);
  } catch {
    return null;
  }
}

export async function updateMe(values) {
  const { data: sessionData } = await supabase.auth.getUser();
  const user = sessionData?.user;
  if (!user) throw new Error('Not authenticated');

  // Only the columns a user owns. role, review_count and badge_level are
  // rejected by the guard trigger anyway; not sending them keeps that from
  // being the thing standing between a user and an admin role.
  const allowed = ['display_name', 'full_name', 'bio', 'avatar_url'];
  const patch = Object.fromEntries(
    Object.entries(values).filter(([key]) => allowed.includes(key)),
  );

  const { error } = await supabase.from('profiles').update(patch).eq('id', user.id);
  if (error) throw error;

  return hydrate(user);
}

// Thrown by register() when the address already has a confirmed account.
export const EMAIL_ALREADY_REGISTERED = 'email_already_registered';

export async function register({ email, password }) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo: redirectUrl('/') },
  });
  if (error) throw error;

  // Signing up an address that already has a *confirmed* account is not an
  // error to Supabase: its user-enumeration protection answers with an
  // obfuscated user carrying no identities, and mails no code. Left
  // unchecked, the caller would send the visitor to an OTP screen waiting on
  // a code that never arrives. An existing but unconfirmed account still
  // comes back with its identity, so this only catches the duplicate.
  if (Array.isArray(data?.user?.identities) && data.user.identities.length === 0) {
    throw Object.assign(
      new Error('An account with this email already exists. Please log in instead.'),
      { code: EMAIL_ALREADY_REGISTERED },
    );
  }

  return data;
}

export async function verifyOtp({ email, otpCode }) {
  const { data, error } = await supabase.auth.verifyOtp({
    email,
    token: otpCode,
    type: 'signup',
  });
  if (error) throw error;
  return data;
}

export async function resendOtp(email) {
  const { error } = await supabase.auth.resend({ type: 'signup', email });
  if (error) throw error;
}

export async function loginViaEmailPassword(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function loginWithProvider(provider, returnTo = '/') {
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo: redirectUrl(returnTo) },
  });
  if (error) throw error;
}

export async function resetPasswordRequest(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: redirectUrl('/reset-password'),
  });
  if (error) throw error;
}

// Supabase turns a recovery link into a session before this runs, so setting
// the new password is an ordinary updateUser call.
export async function setNewPassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function logout(redirectTo = '/') {
  await supabase.auth.signOut();
  window.location.href = redirectTo;
}

export function redirectToLogin(returnTo = window.location.pathname) {
  const target = `${returnTo}${window.location.search}`;
  window.location.href = `/login?returnTo=${encodeURIComponent(target)}`;
}

export function onAuthStateChange(callback) {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
  return () => data.subscription.unsubscribe();
}

export const auth = {
  me,
  currentUser,
  updateMe,
  register,
  verifyOtp,
  resendOtp,
  loginViaEmailPassword,
  loginWithProvider,
  resetPasswordRequest,
  setNewPassword,
  logout,
  redirectToLogin,
  onAuthStateChange,
};
